import {
  NEXT_SHIPMENT_STEP,
  PREVIOUS_SHIPMENT_STEP,
  GO_TO_SHIPMENT_STEP,
  SET_SHIPMENT_FORM_PRISTINE,
  SET_AFTER_SUBMIT_ACTION,
  PROCESS_AFTER_SUBMIT_ACTION,
  SET_SHIPMENT,
  SET_DELIVERY,
  SET_SHIPMENT_CARGO,
  SET_FILES,
  FETCH_SHIPMENT_DELIVERY,
  FETCH_SHIPMENT,
  SUCCESS,
  RESET_SHIPMENT_FORM_DATA,
  SET_NOTIFICATION_CONTACTS,
  SET_RECIPIENTS,
  SET_SHIPMENT_FORM_STATUS,
  SET_JUST_SELECTED_TAB
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { deleteShipmentFiles, postShipmentFiles } from './files';
import { createShipment, editShipment } from './dashboard';
import { postDelivery, editDelivery } from './delivery';
import { postCargo, editCargo } from './cargo';
import { putShipmentNotificationRecipients } from './recipients';
import { getRequestOptions, requestStatuses as statuses } from '#helper';

export const nextStep = () => dispatch => {
  dispatch({ type: NEXT_SHIPMENT_STEP });
};

export const previousStep = () => dispatch => {
  dispatch({ type: PREVIOUS_SHIPMENT_STEP });
};

export const goToStep = step => dispatch => {
  dispatch({ type: GO_TO_SHIPMENT_STEP, payload: step });
};

export const setPristine = (form, value) => dispatch => {
  dispatch({
    type: SET_SHIPMENT_FORM_PRISTINE,
    payload: { form, value }
  });
};

export const setAfterSubmitAction = fn => dispatch => {
  dispatch({ type: SET_AFTER_SUBMIT_ACTION, payload: fn });
};

export const processAfterSubmitAction = () => async (dispatch, getState) => {
  const { shipmentForm: { afterSubmitAction } } = getState();

  if (afterSubmitAction && typeof afterSubmitAction === 'function') {
    try {
      await afterSubmitAction();
    } catch (e) {
      // request errors are already reported through the snackbar
      dispatch({ type: PROCESS_AFTER_SUBMIT_ACTION });
      dispatch({ type: SET_SHIPMENT_FORM_STATUS, payload: statuses.failed });
      return;
    }
  }

  dispatch({ type: PROCESS_AFTER_SUBMIT_ACTION });
  dispatch({ type: SET_SHIPMENT_FORM_STATUS, payload: statuses.succeeded });
};

export const setShipment = (id, data, states) => dispatch => {
  const { devices, uid, ...rest } = data;
  dispatch({ type: SET_SHIPMENT, payload: { id, ...rest } });
  dispatch({ type: SET_DELIVERY, payload: states });

  dispatch({
    type: FETCH_SHIPMENT_DELIVERY,
    payload: states
  });

  // Keep state.shipment.defaultDevices in sync so the Cargo form (step 3)
  // can read the devices from mapStateToProps. Without this dispatch,
  // shipment.defaultDevices stays empty and cargo initialValues has no devices.
  if (devices?.length) {
    dispatch({
      type: FETCH_SHIPMENT,
      payload: { ...rest, id, devices }
    });
  }
};

export const setDelivery = delivery => dispatch => {
  dispatch({ type: SET_DELIVERY, payload: delivery });

  dispatch({
    type: FETCH_SHIPMENT_DELIVERY,
    payload: delivery
  });
};

export const setCargo = (cargo, shipment) => dispatch => {
  const cargoToSet = cargo.map(({ id: cargoId, devices, projectAlertId, ...rest }) => {
    const cargoWithoutAlerts = {
      ...rest,
      devices: devices.map(d => d.id),
      id: cargoId
    };
    if (projectAlertId) {
      const alerts = devices.reduce((acc, d) => (d.alertOn ? [...acc, d.id] : acc), []);
      return {
        ...cargoWithoutAlerts,
        projectAlertId,
        alerts: alerts.length ? alerts : undefined
      };
    }
    return cargoWithoutAlerts;
  });
  dispatch({ type: SET_SHIPMENT_CARGO, payload: cargoToSet });

  dispatch({
    type: FETCH_SHIPMENT,
    payload: {
      ...shipment,
      devices: cargo.flatMap(c => c.devices.map(d => ({
        id: d.id,
        uid: d.uid,
        title: d.label,
        cargoId: c.id
      })))
    }
  });
};

export const setFiles = (filesToPost, filesToDelete) => dispatch => {
  dispatch({ type: SET_FILES, payload: { filesToPost, filesToDelete } });
};

export const setRecipients = (recipients, formItems) => dispatch => {
  dispatch({ type: SET_RECIPIENTS, payload: recipients, formItems });
};

export const fetchNotificationContacts = () => async dispatch => {
  let abortController = new AbortController();

  try {
    abortController.abort();
    const notificationContactsUrl = webPortalApi.buildApiUrl('shipment/notification/contact');
    abortController = new AbortController();
    const { signal } = abortController;
    const res = await fetch(notificationContactsUrl, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }
    const notificationContacts = await res.json();

    dispatch({
      type: SET_NOTIFICATION_CONTACTS,
      payload: notificationContacts
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return false;
    }
  }
  return true;
};

export const processForm = () => async (dispatch, getState) => {
  const state = getState();
  const {
    shipment: { id: shipmentId, ...shipmentData },
    recipients,
    delivery,
    cargo,
    files: { filesToPost, filesToDelete },
    pristine
  } = state.shipmentForm;
  let id = shipmentId || state.shipment?.id;

  const requests = [];

  const formsMap = {
    shipment: {
      formName: 'EditShipmentForm',
      editFn: editShipment
    },
    delivery: {
      formName: 'ShipmentDeliveryForm',
      editFn: editDelivery
    },
    cargo: {
      formName: 'ShipmentCargoForm',
      editFn: editCargo
    },
    recipients: {
      formName: 'ShipmentNotificationForm',
      editFn: putShipmentNotificationRecipients
    }
  };

  if (Object.keys(shipmentData).length > 0 || id) {
    if (id) {
      Object.keys(formsMap).forEach(key => {
        const { formName, editFn } = formsMap[key];
        const isPristineForm = pristine[formName];
        if (Object.prototype.hasOwnProperty.call(pristine, formName) && !isPristineForm) {
          const currentFormData = state.shipmentForm[key];
          requests.push(dispatch(editFn(id, currentFormData)));
          if (formName === 'EditShipmentForm') {
            const deliveryFormData = state.shipmentForm.delivery;
            requests.push(dispatch(editDelivery(id, deliveryFormData)));
          }
        }
      });
    } else {
      const { id: newId, success } = await dispatch(createShipment(shipmentData));
      if (success) {
        id = newId;
      }
    }
  }

  const isCreate = !shipmentId && !state.shipment?.id;

  if (id && delivery.length > 0 && isCreate) {
    const deliveryWithoutId = delivery.map(({ id: genId, ...rest }) => ({ ...rest }));
    requests.push(dispatch(postDelivery(id, deliveryWithoutId)));
  }

  if (id && cargo.length > 0 && isCreate) {
    requests.push(dispatch(postCargo(id, cargo)));
  }

  if (id && recipients.length > 0 && isCreate) {
    requests.push(dispatch(putShipmentNotificationRecipients(id, recipients)));
  }

  if (id && filesToDelete.length > 0) {
    requests.push(dispatch(deleteShipmentFiles(id, filesToDelete)));
  }

  const results = await Promise.all(requests);

  if (id && filesToPost.length > 0) {
    const res = await dispatch(postShipmentFiles(id, filesToPost));
    results.push(res);
  }

  if (results?.every(r => !!r.success)) {
    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Shipment has been saved'
      }
    });
    return { success: true, id };
  }

  return { success: false };
};

export const setFormStatus = status => dispatch => {
  dispatch({ type: SET_SHIPMENT_FORM_STATUS, payload: status });
};

export const resetFormData = () => dispatch => {
  dispatch({ type: RESET_SHIPMENT_FORM_DATA });
};

export const setJustSelectedTab = value => dispatch => {
  dispatch({ type: SET_JUST_SELECTED_TAB, payload: value });
};
